import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { formsAPI, integrationsAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { FeatureGate, FeatureLockedBadge, hasFeatureAccess } from '../components/FeatureGate';
import { FieldElementsSidebar, BrandingBanner, sidebarStyles } from '../components/FormBuilderComponents';
import IconPicker, { IconDisplay } from '../components/IconPicker';
import { 
  Plus, Trash2, GripVertical, Type, Mail, Phone, Calendar, 
  List, CheckSquare, Radio, FileText as FileTextIcon, Save,
  Eye, Settings, Copy, ExternalLink, Plug, History, AlertCircle, 
  CheckCircle, XCircle, RefreshCw, Upload, GitBranch, Layers,
  Palette, Lock, Image, User, MapPin, Globe, Hash, Clock, 
  ListChecks, Link2, ImagePlus, Code, Building2, Info, HelpCircle, Crown,
  BarChart3, PenTool, ShieldCheck, Users, SeparatorHorizontal, ChevronRight, ChevronLeft, Check,
  ChevronDown, Pen, Star
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IntegrationsPanel } from '../components/IntegrationsPanel';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { AnalyticsTab } from '../components/AnalyticsTab';
import { SignaturePad } from '../components/SignaturePad';
import { FormCollaboration } from '../components/FormCollaboration';
import { FieldsLimitPill } from '../components/FieldsLimitPill';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Expanded field types like JotForm — ordered by usage frequency (most-used first)
const fieldTypes = [
  // ── BASIC (top of palette, most-used personal info) ──
  { type: 'heading', label: 'Section Header', icon: Type, category: 'basic' },
  { type: 'name', label: 'Name Fields', icon: User, category: 'basic' },
  { type: 'email', label: 'Email Address', icon: Mail, category: 'basic' },
  { type: 'phone', label: 'Phone/Mobile', icon: Phone, category: 'basic' },
  { type: 'text', label: 'Simple Text', icon: Type, category: 'basic' },
  { type: 'textarea', label: 'Text Area', icon: FileTextIcon, category: 'basic' },

  // ── CHOICE (dropdown / radio / checkbox first) ──
  { type: 'select', label: 'Dropdown', icon: List, category: 'choice' },
  { type: 'radio', label: 'Radio Field', icon: Radio, category: 'choice' },
  { type: 'multiple', label: 'Multiple Choice', icon: ListChecks, category: 'choice' },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare, category: 'choice' },
  { type: 'image_choice', label: 'Image Choice', icon: ImagePlus, category: 'choice', description: 'Select from images' },
  { type: 'icon_choice', label: 'Icon Choice', icon: Layers, category: 'choice', description: 'Cards with icons' },

  // ── ADVANCED (address / date / number first — most-used) ──
  { type: 'address', label: 'Address Fields', icon: MapPin, category: 'advanced' },
  { type: 'date', label: 'Date', icon: Calendar, category: 'advanced' },
  { type: 'number', label: 'Numeric Field', icon: Hash, category: 'advanced' },
  { type: 'time', label: 'Time', icon: Clock, category: 'advanced' },
  { type: 'datetime', label: 'Date & Time', icon: Calendar, category: 'advanced' },
  { type: 'country', label: 'Country List', icon: Globe, category: 'advanced' },
  { type: 'url', label: 'Website URL', icon: Link2, category: 'advanced' },

  // ── UPLOAD (file before image — more common) ──
  { type: 'file', label: 'File Upload', icon: Upload, category: 'upload' },
  { type: 'image', label: 'Image Upload', icon: ImagePlus, category: 'upload' },

  // ── SPECIAL (signature first — heavily used, then layout/protection) ──
  { type: 'signature', label: 'Signature', icon: FileTextIcon, category: 'special' },
  { type: 'captcha', label: 'CAPTCHA', icon: ShieldCheck, category: 'special', description: 'Spam protection' },
  { type: 'page_break', label: 'Page Break', icon: SeparatorHorizontal, category: 'special', description: 'Add new step' },
  { type: 'divider', label: 'Divider', icon: Type, category: 'special' },
  { type: 'html', label: 'Custom HTML', icon: Code, category: 'special' },
];

// Country list for country dropdown
const countries = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh", 
  "Belgium", "Brazil", "Canada", "Chile", "China", "Colombia", "Denmark", "Egypt", 
  "Finland", "France", "Germany", "Greece", "Hong Kong", "India", "Indonesia", "Iran", 
  "Iraq", "Ireland", "Israel", "Italy", "Japan", "Kenya", "Malaysia", "Mexico", 
  "Netherlands", "New Zealand", "Nigeria", "Norway", "Pakistan", "Peru", "Philippines", 
  "Poland", "Portugal", "Russia", "Saudi Arabia", "Singapore", "South Africa", "South Korea", 
  "Spain", "Sri Lanka", "Sweden", "Switzerland", "Taiwan", "Thailand", "Turkey", 
  "UAE", "UK", "Ukraine", "USA", "Vietnam"
];

const conditionOperators = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
];

// Icon Choice Options Editor Component
const IconChoiceOptionsEditor = ({ options, multiSelect, onOptionsChange, onMultiSelectChange }) => {
  const [editingIconIndex, setEditingIconIndex] = useState(null);

  const addOption = () => {
    const newOptions = [...options, {
      icon: { type: 'lucide', value: 'Star' },
      label: `Option ${options.length + 1}`,
      description: ''
    }];
    onOptionsChange(newOptions);
  };

  const updateOption = (index, updates) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], ...updates };
    onOptionsChange(newOptions);
  };

  const deleteOption = (index) => {
    if (options.length <= 1) return;
    const newOptions = options.filter((_, i) => i !== index);
    onOptionsChange(newOptions);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Icon Options</Label>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Multi-select</Label>
          <Switch
            checked={multiSelect}
            onCheckedChange={onMultiSelectChange}
          />
        </div>
      </div>

      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {options.map((option, index) => (
          <div key={index} className="p-3 border rounded-lg bg-slate-50 space-y-2">
            <div className="flex items-start gap-2">
              {/* Icon Button */}
              <button
                type="button"
                onClick={() => setEditingIconIndex(index)}
                className="w-14 h-14 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center bg-white hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex-shrink-0"
                title="Click to change icon"
              >
                <IconDisplay icon={option.icon} className="h-7 w-7 text-indigo-600" />
              </button>

              {/* Label & Description */}
              <div className="flex-1 space-y-2">
                <Input
                  value={option.label}
                  onChange={(e) => updateOption(index, { label: e.target.value })}
                  placeholder="Option title"
                  className="font-medium"
                />
                <Input
                  value={option.description || ''}
                  onChange={(e) => updateOption(index, { description: e.target.value })}
                  placeholder="Description (optional)"
                  className="text-sm"
                />
              </div>

              {/* Delete Button */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => deleteOption(index)}
                disabled={options.length <= 1}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addOption}
        className="w-full text-indigo-600 border-indigo-200 hover:bg-indigo-50"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add Icon Option
      </Button>

      <p className="text-xs text-slate-500">
        Click on the icon box to change icons. {multiSelect ? 'Users can select multiple options.' : 'Users can select one option.'}
      </p>

      {/* Icon Picker Dialog */}
      <Dialog open={editingIconIndex !== null} onOpenChange={() => setEditingIconIndex(null)}>
        <DialogContent className="max-w-md p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg">Choose Icon</DialogTitle>
          </DialogHeader>
          <IconPicker
            selectedIcon={editingIconIndex !== null ? options[editingIconIndex]?.icon?.value : null}
            selectedType={editingIconIndex !== null ? options[editingIconIndex]?.icon?.type : 'image'}
            onSelect={(icon) => {
              if (editingIconIndex !== null) {
                updateOption(editingIconIndex, { icon });
                setEditingIconIndex(null);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SortableField = ({ field, onEdit, onDelete, onMobileEdit, isSelected, onSelect, stepNumber, showStep }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getFieldIcon = () => {
    const fieldType = fieldTypes.find(f => f.type === field.type);
    if (fieldType) {
      const Icon = fieldType.icon;
      return <Icon className="w-4 h-4 text-slate-400" />;
    }
    return null;
  };

  // Special rendering for page break
  if (field.type === 'page_break') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`cursor-pointer ${isSelected ? 'ring-2 ring-indigo-200 rounded-lg' : ''}`}
        onClick={() => onSelect(field.id)}
        data-testid={`field-${field.id}`}
      >
        <div className="relative flex items-center py-2">
          <div className="flex-1 border-t-2 border-dashed border-indigo-300"></div>
          <div className="absolute left-1/2 transform -translate-x-1/2 bg-indigo-100 border-2 border-indigo-300 rounded-full px-4 py-1.5 flex items-center gap-2">
            <div {...attributes} {...listeners} className="cursor-grab">
              <GripVertical className="w-4 h-4 text-indigo-400" />
            </div>
            <SeparatorHorizontal className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-semibold text-indigo-700">{field.label || 'Page Break'}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(field.id);
              }}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0 ml-1"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
          <div className="flex-1 border-t-2 border-dashed border-indigo-300"></div>
        </div>
        <p className="text-center text-xs text-indigo-500 mt-1">Fields below will appear on the next step</p>
      </div>
    );
  }

  // Render live field preview (like JotForm - shows actual sub-fields)
  const renderFieldPreview = () => {
    const inputClass = "h-10 w-full border border-slate-200 rounded bg-white";
    const subLabelClass = "text-xs text-slate-400 mt-1 leading-tight";

    switch (field.type) {
      case 'name':
        return (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <div className={`${inputClass} flex items-center px-3 text-sm text-slate-400`}>{(field.sub_placeholders || {}).first_name || ''}</div>
              <p className={subLabelClass}>{(field.sublabels || {}).first_name || 'First Name'}</p>
            </div>
            <div>
              <div className={`${inputClass} flex items-center px-3 text-sm text-slate-400`}>{(field.sub_placeholders || {}).last_name || ''}</div>
              <p className={subLabelClass}>{(field.sublabels || {}).last_name || 'Last Name'}</p>
            </div>
          </div>
        );
      case 'address': {
        const addrLabelClass = "text-xs font-medium text-slate-600 mb-1.5 leading-tight";
        return (
          <div className="space-y-2.5 mt-3">
            {/* Country */}
            <div>
              <p className={addrLabelClass}>{(field.sublabels || {}).country || 'Country'}</p>
              <div className={`${inputClass} flex items-center justify-between px-3 text-sm text-slate-400`}>
                <span>Select country</span>
                <span className="text-slate-300">▾</span>
              </div>
            </div>
            {/* Street with pin icon */}
            <div>
              <p className={addrLabelClass}>{(field.sublabels || {}).street || 'Street Address'}</p>
              <div className={`${inputClass} flex items-center px-3 text-sm text-slate-400 gap-2`}>
                <span className="text-slate-300">📍</span>
                <span>{(field.sub_placeholders || {}).street || 'Start typing your address...'}</span>
              </div>
            </div>
            {/* State + City row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className={addrLabelClass}>{(field.sublabels || {}).state || 'State / Province'}</p>
                <div className={`${inputClass} flex items-center justify-between px-3 text-sm text-slate-400`}>
                  <span>Select state</span>
                  <span className="text-slate-300">▾</span>
                </div>
              </div>
              <div>
                <p className={addrLabelClass}>{(field.sublabels || {}).city || 'City / Suburb'}</p>
                <div className={`${inputClass} flex items-center px-3 text-sm text-slate-400`}>{(field.sub_placeholders || {}).city || 'City or suburb'}</div>
              </div>
            </div>
            {/* Postal */}
            <div>
              <p className={addrLabelClass}>
                {(field.sublabels || {}).zip || 'Postal / Zip Code'}{' '}
                <span className="text-slate-400 font-normal">(optional)</span>
              </p>
              <div className={`${inputClass} flex items-center px-3 text-sm text-slate-400`}>{(field.sub_placeholders || {}).zip || 'Optional'}</div>
            </div>
          </div>
        );
      }
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
      case 'url': {
        const previewText = field.read_only && field.default_value
          ? field.default_value
          : (field.placeholder || (field.type === 'email' ? 'name@example.com' : field.type === 'phone' ? '+1 (555) 000-0000' : field.type === 'url' ? 'https://' : field.type === 'number' ? '0' : 'Type here...'));
        const isMulti = typeof previewText === 'string' && previewText.includes('\n');
        return (
          <div className="mt-3">
            <div
              className={`w-full border border-slate-200 rounded bg-white px-3 text-sm text-slate-400 ${
                isMulti ? 'py-2 whitespace-pre-wrap font-mono' : 'h-10 flex items-center'
              }`}
            >
              {previewText}
            </div>
          </div>
        );
      }
      case 'textarea': {
        const previewText = field.read_only && field.default_value
          ? field.default_value
          : (field.placeholder || 'Type your answer here...');
        return (
          <div className="mt-3">
            <div
              className={`w-full border border-slate-200 rounded bg-white px-3 py-2.5 text-sm text-slate-400 whitespace-pre-wrap ${
                field.read_only ? 'font-mono' : 'min-h-[6rem]'
              }`}
            >
              {previewText}
            </div>
          </div>
        );
      }
      case 'date':
      case 'time':
      case 'datetime':
        return (
          <div className="mt-3">
            <div className="h-10 w-full border border-slate-200 rounded bg-white flex items-center px-3 text-sm text-slate-400 justify-between">
              <span>{field.type === 'time' ? 'HH:MM' : field.type === 'datetime' ? 'Select date & time' : field.date_format || 'DD/MM/YYYY'}</span>
              <Calendar className="w-4 h-4 text-slate-300" />
            </div>
          </div>
        );
      case 'select':
        return (
          <div className="mt-3">
            <div className={`h-10 w-full border border-slate-200 rounded bg-white flex items-center justify-between px-3 text-sm ${field.default_value ? 'text-slate-700' : 'text-slate-400'}`}>
              <span>{field.default_value || 'Select an option'}</span>
              <ChevronDown className="w-4 h-4 text-slate-300" />
            </div>
          </div>
        );
      case 'country':
        return (
          <div className="mt-3">
            <div className="h-10 w-full border border-slate-200 rounded bg-white flex items-center justify-between px-3 text-sm text-slate-400">
              <span>Select country</span>
              <ChevronDown className="w-4 h-4 text-slate-300" />
            </div>
          </div>
        );
      case 'radio':
        return (
          <div className="mt-3 space-y-2">
            {(field.options || []).slice(0, 3).map((opt, i) => {
              const isDefault = field.default_value === opt;
              return (
                <div key={i} className="flex items-center gap-2.5 py-1">
                  <span
                    className={`w-4.5 h-4.5 rounded-full flex-shrink-0 flex items-center justify-center ${isDefault ? 'border-2 border-indigo-500 bg-white' : 'border-2 border-slate-300'}`}
                    style={{ width: '18px', height: '18px' }}
                  >
                    {isDefault && <span className="w-2 h-2 rounded-full bg-indigo-500" />}
                  </span>
                  <span className="text-sm text-slate-600">{opt}</span>
                </div>
              );
            })}
            {(field.options || []).length > 3 && <p className="text-xs text-slate-400">+{field.options.length - 3} more</p>}
          </div>
        );
      case 'checkbox':
      case 'multiple': {
        const defaultValues = Array.isArray(field.default_values) ? field.default_values : [];
        return (
          <div className="mt-3 space-y-2">
            {(field.options || []).slice(0, 3).map((opt, i) => {
              const isDefault = defaultValues.includes(opt);
              return (
                <div key={i} className="flex items-center gap-2.5 py-1">
                  <span
                    className={`w-4.5 h-4.5 rounded flex-shrink-0 flex items-center justify-center ${isDefault ? 'border-2 border-indigo-500 bg-indigo-500' : 'border-2 border-slate-300'}`}
                    style={{ width: '18px', height: '18px' }}
                  >
                    {isDefault && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </span>
                  <span className="text-sm text-slate-600">{opt}</span>
                </div>
              );
            })}
            {(field.options || []).length > 3 && <p className="text-xs text-slate-400">+{field.options.length - 3} more</p>}
          </div>
        );
      }
      case 'file':
      case 'image':
        return (
          <div className="mt-3">
            <div className="h-24 w-full border-2 border-dashed border-slate-200 rounded bg-slate-50 flex flex-col items-center justify-center">
              <Upload className="w-6 h-6 text-slate-300 mb-1" />
              <p className="text-xs text-slate-400">Click to upload {field.type === 'image' ? 'image' : 'file'}</p>
            </div>
          </div>
        );
      case 'signature':
        return (
          <div className="mt-3">
            <div className="h-28 w-full border-2 border-dashed border-slate-200 rounded bg-slate-50 flex flex-col items-center justify-center">
              <Pen className="w-6 h-6 text-slate-300 mb-1" />
              <p className="text-xs text-slate-400">Draw signature here</p>
            </div>
          </div>
        );
      case 'heading':
        return (
          <div className="mt-2">
            <span className="text-xl font-semibold text-slate-700">{field.label}</span>
          </div>
        );
      case 'divider':
        return <hr className="mt-3 border-slate-200" />;
      case 'static_text':
        return (
          <div className="mt-2">
            <p className="text-sm text-slate-500 line-clamp-2">{field.static_content || 'Static text content...'}</p>
          </div>
        );
      case 'image_choice':
        return (
          <div className="mt-3 grid grid-cols-3 gap-2">
            {(field.image_options || []).slice(0, 3).map((opt, i) => (
              <div key={i} className="border border-slate-200 rounded overflow-hidden">
                {opt.image_url ? (
                  <img src={opt.image_url} alt={opt.label} className="w-full h-14 object-cover" />
                ) : (
                  <div className="w-full h-14 bg-slate-100 flex items-center justify-center"><Image className="w-5 h-5 text-slate-300" /></div>
                )}
                <p className="text-xs text-slate-500 text-center py-1 truncate px-1">{opt.label}</p>
              </div>
            ))}
          </div>
        );
      case 'icon_choice':
        return (
          <div className="mt-3 grid grid-cols-3 gap-2">
            {(field.icon_options || []).slice(0, 3).map((opt, i) => (
              <div key={i} className="border border-slate-200 rounded p-2.5 text-center">
                <p className="text-xs text-slate-600 truncate">{opt.label}</p>
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border rounded-lg p-4 pb-5 cursor-pointer hover:border-indigo-300 hover:shadow-md transition-all ${
        isSelected ? 'border-indigo-500 ring-2 ring-indigo-100 shadow-sm' : 'border-slate-200 shadow-sm'
      }`}
      onClick={() => onSelect(field.id)}
      data-testid={`field-${field.id}`}
    >
      <div className="flex items-start justify-between mb-0">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <div {...attributes} {...listeners} className="cursor-grab flex-shrink-0" data-testid={`drag-handle-${field.id}`}>
            <GripVertical className="w-4 h-4 text-slate-300" />
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
            {getFieldIcon()}
            <span className="font-semibold text-slate-800 text-base">{field.label}</span>
            {field.required && <span className="text-red-500 text-xs font-bold">*</span>}
          </div>
          {showStep && (
            <Badge variant="outline" className="text-[10px] flex-shrink-0">Step {field.step || 1}</Badge>
          )}
          {field.condition && (
            <Badge variant="secondary" className="text-[10px] flex-shrink-0">
              <GitBranch className="w-3 h-3 mr-0.5" />
              Conditional
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Mobile-only ⚙️ Settings icon — opens field properties sheet */}
          {onMobileEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onMobileEdit(field.id);
              }}
              className="lg:hidden text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-7 w-7 p-0"
              data-testid={`mobile-field-settings-${field.id}`}
              aria-label="Edit field properties"
            >
              <Settings className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(field.id);
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
            data-testid={`delete-field-${field.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      {renderFieldPreview()}
    </div>
  );
};

export const FormBuilderPage = () => {
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { usage, user } = useAuth();
  const currentPlan = usage?.plan || 'free';
  const [form, setForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('build');
  const [showCollabModal, setShowCollabModal] = useState(false);
  // Mobile UX (JotForm-style): both side panels are sheets on small screens
  const [mobilePaletteOpen, setMobilePaletteOpen] = useState(false);
  const [mobilePropsOpen, setMobilePropsOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    loadForm();
    
    const integrationStatus = searchParams.get('status');
    const integration = searchParams.get('integration');
    if (integrationStatus === 'connected' && integration) {
      toast.success(`${integration.replace('_', ' ')} connected successfully!`);
      setActiveTab('integrations');
      navigate(`/forms/${formId}/builder`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, searchParams]);

  // Auto-save every 2 minutes to prevent data loss
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const autoSaveRef = useRef(null);
  
  useEffect(() => {
    // Only auto-save if there are fields and form is loaded
    if (!form || fields.length === 0) return;
    
    // Clear any existing timer
    if (autoSaveRef.current) {
      clearInterval(autoSaveRef.current);
    }
    
    // Set up auto-save interval (every 2 minutes)
    autoSaveRef.current = setInterval(async () => {
      try {
        let fieldsToSave = fields;
        if (form?.enable_steps && fields.some(f => f.type === 'page_break')) {
          fieldsToSave = recalculateSteps(fields);
        }
        await formsAPI.update(formId, { fields: fieldsToSave });
        setLastAutoSave(new Date());
        console.log('Auto-saved at', new Date().toLocaleTimeString());
      } catch (error) {
        console.error('Auto-save failed:', error);
        // Don't show toast for auto-save failures to avoid annoying users
      }
    }, 2 * 60 * 1000); // 2 minutes
    
    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
      }
    };
  }, [form, fields, formId]);

  // Recalculate step numbers for all fields based on page break positions
  const recalculateSteps = (fieldList) => {
    let currentStep = 1;
    return fieldList.map((field, index) => {
      if (field.type === 'page_break') {
        // Page break marks the END of current step
        const updatedField = { 
          ...field, 
          step: currentStep,
          label: `Page ${currentStep + 1}` // Label shows what page comes AFTER this break
        };
        currentStep++; // Increment for next fields
        return updatedField;
      }
      return { ...field, step: currentStep };
    });
  };

  const loadForm = async () => {
    try {
      const response = await formsAPI.getOne(formId);
      setForm(response.data);
      // Recalculate steps to ensure consistent step numbers after loading
      const loadedFields = response.data.fields || [];
      if (response.data.enable_steps && loadedFields.some(f => f.type === 'page_break')) {
        setFields(recalculateSteps(loadedFields));
      } else {
        setFields(loadedFields);
      }
    } catch (error) {
      toast.error('Failed to load form');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const addField = (type) => {
    // Page break automatically enables multi-step and creates new step
    if (type === 'page_break') {
      if (!form?.enable_steps) {
        setForm({ ...form, enable_steps: true });
        handleFormSettingsUpdate({ enable_steps: true });
        toast.success('Multi-step enabled! Fields below page break will be on the next step.');
      }
      
      // Count existing page breaks to determine the step number
      const pageBreakCount = fields.filter(f => f.type === 'page_break').length;
      
      // Add page break field
      const newField = {
        id: `field-${Date.now()}`,
        type: 'page_break',
        label: `Page ${pageBreakCount + 2}`, // +2 because first page is 1, this creates page 2, 3, etc.
        step: pageBreakCount + 1, // Page break belongs to the current step
      };
      
      const newFields = [...fields, newField];
      setFields(recalculateSteps(newFields));
      setSelectedField(newField.id);
      toast.success(`Page break added. Next fields will be on Step ${pageBreakCount + 2}`);
      return;
    }
    
    const defaultLabels = {
      name: 'Full Name',
      email: 'Email Address',
      text: 'Text Field',
      textarea: 'Message',
      phone: 'Phone Number',
      address: 'Address',
      country: 'Country',
      number: 'Number',
      date: 'Date',
      time: 'Time',
      datetime: 'Date & Time',
      url: 'Website',
      select: 'Select Option',
      radio: 'Choose One',
      checkbox: 'Checkbox',
      multiple: 'Multiple Choice',
      image_choice: 'Select an Image',
      icon_choice: 'Select a Category',
      file: 'Upload File',
      image: 'Upload Image',
      signature: 'Signature',
      heading: 'Section Title',
      static_text: 'Information',
      divider: '',
      html: 'Custom Content',
    };
    
    // Calculate current step based on last page break
    let currentStep = 1;
    if (form?.enable_steps) {
      const pageBreaks = fields.filter(f => f.type === 'page_break');
      currentStep = pageBreaks.length + 1;
    }
    
    const newField = {
      id: `field-${Date.now()}`,
      type,
      label: defaultLabels[type] || `New ${type} field`,
      placeholder: '',
      required: false,
      options: ['select', 'radio', 'checkbox', 'multiple'].includes(type) ? ['Option 1', 'Option 2', 'Option 3'] : null,
      // Image choice field - array of {label, image_url} objects
      image_options: type === 'image_choice' ? [
        { label: 'Option 1', image_url: '' },
        { label: 'Option 2', image_url: '' },
        { label: 'Option 3', image_url: '' },
      ] : null,
      // Icon choice field - array of {icon, label, description} objects
      icon_options: type === 'icon_choice' ? [
        { icon: { type: 'image', value: 'https://raw.githubusercontent.com/microsoft/fluentui-emoji/main/assets/Bar%20chart/3D/bar_chart_3d.png', name: 'Bar Chart' }, label: 'Option 1', description: 'Description for option 1' },
        { icon: { type: 'image', value: 'https://raw.githubusercontent.com/microsoft/fluentui-emoji/main/assets/Busts%20in%20silhouette/3D/busts_in_silhouette_3d.png', name: 'Team' }, label: 'Option 2', description: 'Description for option 2' },
        { icon: { type: 'image', value: 'https://raw.githubusercontent.com/microsoft/fluentui-emoji/main/assets/Direct%20hit/3D/direct_hit_3d.png', name: 'Target' }, label: 'Option 3', description: 'Description for option 3' },
      ] : null,
      // Icon choice settings
      icon_choice_multi_select: type === 'icon_choice' ? false : null,
      step: currentStep,
      condition: null,
      // File/image upload defaults
      max_file_size_mb: ['file', 'image'].includes(type) ? 10 : null,
      allowed_file_types: type === 'file' ? ['pdf', 'jpg', 'png', 'doc', 'docx'] : type === 'image' ? ['jpg', 'jpeg', 'png', 'gif', 'webp'] : null,
      // Special field content
      html_content: type === 'html' ? '<p>Enter your custom HTML here</p>' : null,
      static_content: type === 'static_text' ? 'Enter your information text here. This text will be displayed to users but they cannot edit it.' : null,
      heading_size: type === 'heading' ? 'h2' : null,
      // Signature field defaults
      signature_size: type === 'signature' ? 'default' : null,
      // Layout
      width: 'full',
      new_row: false,
    };
    
    // Add field and recalculate steps if multi-step is enabled
    const newFields = [...fields, newField];
    if (form?.enable_steps && newFields.some(f => f.type === 'page_break')) {
      setFields(recalculateSteps(newFields));
    } else {
      setFields(newFields);
    }
    setSelectedField(newField.id);
    // Close the mobile palette sheet (if open) — and don't auto-open the
    // properties sheet here so the user sees their new field land on the canvas first.
    setMobilePaletteOpen(false);
    toast.success('Field added');
  };

  const updateField = (fieldId, updates) => {
    setFields(prev => prev.map(field => field.id === fieldId ? { ...field, ...updates } : field));
  };

  const deleteField = (fieldId) => {
    const fieldToDelete = fields.find(f => f.id === fieldId);
    const newFields = fields.filter(field => field.id !== fieldId);
    
    // If we deleted a page break, recalculate all steps
    if (fieldToDelete?.type === 'page_break') {
      setFields(recalculateSteps(newFields));
    } else {
      setFields(newFields);
    }
    
    if (selectedField === fieldId) setSelectedField(null);
    toast.success('Field removed');
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setFields((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex);
        // Recalculate step numbers after reordering
        return recalculateSteps(reordered);
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Recalculate steps before saving if multi-step is enabled
      let fieldsToSave = fields;
      if (form?.enable_steps && fields.some(f => f.type === 'page_break')) {
        fieldsToSave = recalculateSteps(fields);
        setFields(fieldsToSave); // Update local state too
      }
      await formsAPI.update(formId, { fields: fieldsToSave });
      toast.success('Form saved successfully!');
    } catch (error) {
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handleFormSettingsUpdate = async (updates) => {
    try {
      // NOTE: We intentionally do NOT call setForm(response.data) here.
      // Local state already has the latest user input — overwriting it with
      // the server response was erasing whatever the user was mid-typing in
      // another field. We just fire-and-forget the persistence.
      await formsAPI.update(formId, updates);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const copyFormUrl = () => {
    const shortId = form?.short_id || formId;
    // Use the /api/share/{short_id} endpoint so social platforms (WhatsApp, Slack,
    // LinkedIn, iMessage…) render a link preview with the FORM'S brand identity
    // (logo, company name, form title) instead of generic Formlio metadata.
    // Humans get auto-redirected to the actual public form at /f/{short_id}.
    const url = `${window.location.origin}/api/share/${shortId}`;
    navigator.clipboard.writeText(url);
    toast.success('Form share link copied — link previews will show your brand!');
  };

  // Get unique steps from fields
  const getSteps = () => {
    if (!form?.enable_steps) return [1];
    const steps = [...new Set(fields.map(f => f.step || 1))].sort((a, b) => a - b);
    return steps.length > 0 ? steps : [1];
  };

  // Auto-save and preview
  const handlePreview = async () => {
    setSaving(true);
    try {
      let fieldsToSave = fields;
      if (form?.enable_steps && fields.some(f => f.type === 'page_break')) {
        fieldsToSave = recalculateSteps(fields);
        setFields(fieldsToSave);
      }
      await formsAPI.update(formId, { fields: fieldsToSave });
      toast.success('Form saved!');
      // Open preview in new tab after save
      window.open(`/f/${form?.short_id || formId}`, '_blank');
    } catch (error) {
      toast.error('Failed to save form before preview');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const selectedFieldData = fields.find(f => f.id === selectedField);
  const formUrl = `${window.location.origin}/f/${form?.short_id || formId}`;
  // Public share link routed through the backend so social platforms (WhatsApp,
  // Slack, LinkedIn, iMessage…) get dynamic Open Graph meta tags (form's logo,
  // company name & title) instead of generic Formlio metadata. Humans are
  // 302-redirected to the SPA form route automatically.
  const shareUrl = `${window.location.origin}/api/share/${form?.short_id || formId}`;
  const steps = getSteps();

  // Form elements palette content — used by both desktop sidebar AND mobile sheet
  const formElementsPaletteContent = (
    <>
      <div className="p-3 pb-8 space-y-3">
        {['basic', 'choice', 'advanced', 'upload', 'special'].map(cat => {
          const catFields = fieldTypes.filter(f => f.category === cat);
          if (catFields.length === 0) return null;
          const catLabel = { basic: 'BASIC FIELDS', choice: 'CHOICE FIELDS', advanced: 'ADVANCED', upload: 'UPLOADS', special: 'SPECIAL' }[cat];
          return (
            <div key={cat}>
              <p className="text-[11px] font-bold tracking-widest mb-2 px-2.5 text-slate-500 uppercase">{catLabel}</p>
              <div className="space-y-0.5">
                {catFields.map((ft) => {
                  const Icon = ft.icon;
                  return (
                    <button key={ft.type} onClick={() => addField(ft.type)} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-slate-300 hover:bg-slate-700/80 transition-colors" data-testid={`add-${ft.type}-btn`}>
                      <Icon className="w-4.5 h-4.5 text-slate-400" /><span className="text-[15px]">{ft.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <DashboardLayout fullWidth>
      <div className="space-y-4">
        {/* 3-column layout - sidebars always present to maintain consistent center width */}
        <div className="flex" style={{ columnGap: 'clamp(24px, 6vw, 150px)' }}>
          {/* LEFT SIDEBAR placeholder for header alignment */}
          <div className="hidden lg:block w-64 shrink-0" />

          {/* CENTER: Header with form title and buttons */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-heading font-bold text-slate-900 truncate" data-testid="form-builder-title">{form.title}</h1>
                  <FieldsLimitPill fieldsCount={fields.length} />
                </div>
                <p className="text-sm text-slate-600 truncate">{form.description || 'No description'}</p>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <Button variant="outline" size="sm" onClick={copyFormUrl} className="hidden sm:flex" data-testid="copy-url-btn">
                  <Copy className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Copy URL</span>
                </Button>
                <Button variant="outline" size="sm" onClick={handlePreview} disabled={saving} data-testid="preview-form-btn">
                  <ExternalLink className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Preview</span>
                </Button>
                <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-form-btn">
                  <Save className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save'}</span>
                </Button>
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR placeholder for header alignment */}
          <div className="hidden lg:block w-72 shrink-0" />
        </div>

        {/* 3-column layout - sidebars always present to maintain consistent center width */}
        <div className="flex" style={{ columnGap: 'clamp(24px, 6vw, 150px)' }}>
          {/* LEFT SIDEBAR: Form Elements - content only on Build tab, placeholder otherwise */}
          <div className="hidden lg:block w-64 shrink-0 sticky top-20 self-start h-[calc(100vh-110px)] overflow-y-auto rounded-xl form-builder-scroll" style={{ backgroundColor: activeTab === 'build' ? '#1E293B' : 'transparent' }}>
            {activeTab === 'build' && (
              <>
                <div className="p-4 border-b border-slate-700">
                  <h3 className="font-semibold text-white text-base tracking-wide">Form Elements</h3>
                </div>
                {formElementsPaletteContent}
              </>
            )}
          </div>

          {/* CENTER: Tabs + Content */}
          <div className="flex-1 min-w-0">
            <Tabs
              value={activeTab}
              onValueChange={(nextTab) => {
                // Flush any pending Brand Identity / Design / Settings edits silently
                // when the user switches tabs. This replaces aggressive per-blur saves.
                if (form && nextTab !== activeTab) {
                  const snapshot = {
                    title: form.title,
                    description: form.description,
                    company_name: form.company_name,
                    business_address: form.business_address,
                    business_email: form.business_email,
                    business_phone: form.business_phone,
                    business_website: form.business_website,
                    logo_url: form.logo_url,
                    theme: form.theme,
                  };
                  formsAPI.update(formId, snapshot).catch(() => {});
                }
                setActiveTab(nextTab);
              }}
              className="w-full"
            >
              <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                <TabsList className="inline-flex w-auto min-w-full md:grid md:w-full md:grid-cols-7 gap-1">
                  <TabsTrigger value="build" className="whitespace-nowrap text-xs md:text-sm" data-testid="tab-build">Build</TabsTrigger>
                  <TabsTrigger value="design" className="whitespace-nowrap text-xs md:text-sm" data-testid="tab-design">Design</TabsTrigger>
                  <TabsTrigger value="integrations" className="whitespace-nowrap text-xs md:text-sm" data-testid="tab-integrations">Integrations</TabsTrigger>
                  <TabsTrigger value="analytics" className="whitespace-nowrap text-xs md:text-sm" data-testid="tab-analytics">Analytics</TabsTrigger>
                  <TabsTrigger value="logs" className="whitespace-nowrap text-xs md:text-sm" data-testid="tab-logs">Logs</TabsTrigger>
                  <TabsTrigger value="settings" className="whitespace-nowrap text-xs md:text-sm" data-testid="tab-settings">Settings</TabsTrigger>
                  <TabsTrigger value="share" className="whitespace-nowrap text-xs md:text-sm" data-testid="tab-share">Share</TabsTrigger>
                </TabsList>
              </div>

              {/* Build Tab - Canvas only */}
              <TabsContent value="build" className="mt-5">
                <div className="bg-slate-50/80 border border-slate-200 rounded-xl p-5 lg:p-7 min-h-[300px] lg:min-h-[500px]">
                  {fields.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => setMobilePaletteOpen(true)}
                      className="flex flex-col items-center justify-center h-64 w-full text-center group lg:cursor-default"
                      data-testid="empty-canvas"
                    >
                      <div className="w-16 h-16 rounded-2xl bg-indigo-50 group-active:bg-indigo-100 lg:bg-transparent lg:group-active:bg-transparent flex items-center justify-center mb-4 transition-colors">
                        <Plus className="w-10 h-10 lg:w-16 lg:h-16 text-indigo-500 lg:text-slate-300" />
                      </div>
                      <p className="text-slate-500 text-sm hidden lg:block">Add fields from the left panel to start building your form</p>
                      <p className="text-indigo-600 text-base font-semibold lg:hidden">Tap to add your first field</p>
                      <p className="text-slate-500 text-xs mt-1 lg:hidden">Or use the floating ＋ button at the bottom-right</p>
                    </button>
                  ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                        <div className="flex flex-wrap gap-4">
                          {fields.map((field) => {
                            const w = field.width || 'full';
                            const widthClass = w === 'half' ? 'w-[calc(50%-8px)]' : w === 'third' ? 'w-[calc(33.333%-11px)]' : 'w-full';
                            return (
                              <React.Fragment key={field.id}>
                                {field.new_row && w !== 'full' && <div className="w-full h-0" style={{ margin: 0 }} />}
                                <div className={widthClass}>
                                  <SortableField
                                    field={field}
                                    onEdit={updateField}
                                    onDelete={deleteField}
                                    onMobileEdit={(id) => { setSelectedField(id); setMobilePropsOpen(true); }}
                                    isSelected={selectedField === field.id}
                                    onSelect={setSelectedField}
                                    showStep={form?.enable_steps}
                                  />
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </TabsContent>
          {/* Design Tab - Custom Branding */}
          <TabsContent value="design" className="space-y-6">
            <Card className="p-6">
              <h3 className="font-heading font-semibold mb-4 text-lg flex items-center gap-3">
                <Palette className="w-5 h-5" />
                Custom Branding
                {!hasFeatureAccess(currentPlan, 'growth') && (
                  <FeatureLockedBadge requiredPlan="growth" />
                )}
              </h3>
              <FeatureGate
                requiredPlan="growth"
                currentPlan={currentPlan}
                featureName="Custom Branding"
              >
              <div className="grid lg:grid-cols-12 gap-8">
                <div className="space-y-6 lg:col-span-5">
                  <div className="space-y-2">
                    <Label>Theme Mode</Label>
                    <Select
                      value={form.theme?.mode || 'light'}
                      onValueChange={(value) => {
                        const newTheme = { ...form.theme, mode: value };
                        setForm({ ...form, theme: newTheme });
                        handleFormSettingsUpdate({ theme: newTheme });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="light">Light</SelectItem>
                        <SelectItem value="dark">Dark</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Accent Color</Label>
                    <div className="flex items-center space-x-3">
                      <Input
                        type="color"
                        value={form.theme?.accent_color || '#4F46E5'}
                        onChange={(e) => {
                          const newTheme = { ...form.theme, accent_color: e.target.value };
                          setForm({ ...form, theme: newTheme });
                        }}
                        onBlur={(e) => handleFormSettingsUpdate({ theme: { ...form.theme, accent_color: e.target.value } })}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={form.theme?.accent_color || '#4F46E5'}
                        onChange={(e) => {
                          const newTheme = { ...form.theme, accent_color: e.target.value };
                          setForm({ ...form, theme: newTheme });
                        }}
                        onBlur={(e) => handleFormSettingsUpdate({ theme: { ...form.theme, accent_color: e.target.value } })}
                        className="flex-1"
                        placeholder="#4F46E5"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <div className="flex items-center space-x-3">
                      <Input
                        type="color"
                        value={form.theme?.background_color || '#FFFFFF'}
                        onChange={(e) => {
                          const newTheme = { ...form.theme, background_color: e.target.value };
                          setForm({ ...form, theme: newTheme });
                        }}
                        onBlur={(e) => handleFormSettingsUpdate({ theme: { ...form.theme, background_color: e.target.value } })}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={form.theme?.background_color || '#FFFFFF'}
                        onChange={(e) => {
                          const newTheme = { ...form.theme, background_color: e.target.value };
                          setForm({ ...form, theme: newTheme });
                        }}
                        onBlur={(e) => handleFormSettingsUpdate({ theme: { ...form.theme, background_color: e.target.value } })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Font Family</Label>
                    <Select
                      value={form.theme?.font_family || 'DM Sans'}
                      onValueChange={(value) => {
                        const newTheme = { ...form.theme, font_family: value };
                        setForm({ ...form, theme: newTheme });
                        handleFormSettingsUpdate({ theme: newTheme });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DM Sans">DM Sans</SelectItem>
                        <SelectItem value="Inter">Inter</SelectItem>
                        <SelectItem value="Roboto">Roboto</SelectItem>
                        <SelectItem value="Open Sans">Open Sans</SelectItem>
                        <SelectItem value="Lato">Lato</SelectItem>
                        <SelectItem value="Poppins">Poppins</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Button Style</Label>
                    <Select
                      value={form.theme?.button_style || 'rounded'}
                      onValueChange={(value) => {
                        const newTheme = { ...form.theme, button_style: value };
                        setForm({ ...form, theme: newTheme });
                        handleFormSettingsUpdate({ theme: newTheme });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rounded">Rounded</SelectItem>
                        <SelectItem value="square">Square</SelectItem>
                        <SelectItem value="pill">Pill</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-6 lg:col-span-7">
                  {/* Brand Identity Section - Matches Profile Settings style */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Brand Identity
                      </CardTitle>
                      <CardDescription>This information appears on your documents, signed PDFs, and certificates</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      {/* Logo */}
                      <div className="space-y-2">
                        <Label>Company Logo</Label>
                        <div className="flex items-center gap-4">
                          {form.logo_url ? (
                            <div className="relative w-20 h-20 rounded-lg border border-slate-200 overflow-hidden bg-white flex items-center justify-center">
                              <img src={form.logo_url} alt="Logo" className="w-full h-full object-contain p-1" />
                              <button 
                                type="button" 
                                onClick={() => {
                                  setForm({ ...form, logo_url: '' });
                                  handleFormSettingsUpdate({ logo_url: '' });
                                }} 
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                              >
                                <XCircle className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50">
                              <Building2 className="w-8 h-8 text-slate-300" />
                            </div>
                          )}
                          <div>
                            <label className="cursor-pointer">
                              <Button type="button" variant="outline" size="sm" asChild>
                                <span>
                                  <Upload className="w-4 h-4 mr-2" />
                                  {form.logo_url ? 'Change Logo' : 'Upload Logo'}
                                </span>
                              </Button>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    if (file.size > 2 * 1024 * 1024) {
                                      toast.error('Logo must be less than 2MB');
                                      return;
                                    }
                                    try {
                                      const formDataUpload = new FormData();
                                      formDataUpload.append('file', file);
                                      const response = await fetch(`${API_URL}/api/forms/${formId}/upload`, {
                                        method: 'POST',
                                        body: formDataUpload,
                                      });
                                      if (!response.ok) throw new Error('Upload failed');
                                      const result = await response.json();
                                      const logoUrl = `${API_URL}/api/files/${result.file_id}`;
                                      setForm({ ...form, logo_url: logoUrl });
                                      handleFormSettingsUpdate({ logo_url: logoUrl });
                                      toast.success('Logo uploaded');
                                    } catch (error) {
                                      toast.error('Failed to upload logo');
                                    }
                                  }
                                }}
                              />
                            </label>
                            <p className="text-xs text-slate-500 mt-1">PNG, JPG, or WebP. Max 2MB.</p>
                          </div>
                        </div>
                      </div>

                      {/* Company Name */}
                      <div className="space-y-2">
                        <Label>Company Name *</Label>
                        <Input
                          value={form.company_name || ''}
                          onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                          onBlur={(e) => handleFormSettingsUpdate({ company_name: e.target.value })}
                          placeholder="Acme Corporation"
                          className="h-11"
                        />
                      </div>

                      {/* Business Address */}
                      <div className="space-y-2">
                        <Label>Business Address</Label>
                        <Input
                          value={form.business_address || ''}
                          onChange={(e) => setForm({ ...form, business_address: e.target.value })}
                          onBlur={(e) => handleFormSettingsUpdate({ business_address: e.target.value })}
                          placeholder="123 Business Street, City, State - 400001"
                          className="h-11"
                        />
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> Full address shown in document header
                        </p>
                      </div>

                      {/* Business Email & Phone */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Business Email</Label>
                          <Input
                            type="email"
                            value={form.business_email || ''}
                            onChange={(e) => setForm({ ...form, business_email: e.target.value })}
                            onBlur={(e) => handleFormSettingsUpdate({ business_email: e.target.value })}
                            placeholder="contact@company.com"
                            className="h-11"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Business Phone</Label>
                          <Input
                            type="tel"
                            value={form.business_phone || ''}
                            onChange={(e) => setForm({ ...form, business_phone: e.target.value })}
                            onBlur={(e) => handleFormSettingsUpdate({ business_phone: e.target.value })}
                            placeholder="+91 98765 43210"
                            className="h-11"
                          />
                        </div>
                      </div>

                      {/* Website */}
                      <div className="space-y-2">
                        <Label>Website</Label>
                        <Input
                          type="url"
                          value={form.business_website || ''}
                          onChange={(e) => setForm({ ...form, business_website: e.target.value })}
                          onBlur={(e) => handleFormSettingsUpdate({ business_website: e.target.value })}
                          placeholder="https://www.yourcompany.com"
                          className="h-11"
                          data-testid="brand-website-input"
                        />
                        <p className="text-xs text-slate-500">
                          Shown in document header &amp; included in the downloaded PDF
                        </p>
                      </div>

                      {/* Logo URL alternative */}
                      <div className="space-y-2 pt-3 border-t border-slate-200">
                        <Label className="text-sm text-slate-600">Or enter logo URL</Label>
                        <Input
                          value={form.logo_url || ''}
                          onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                          onBlur={(e) => handleFormSettingsUpdate({ logo_url: e.target.value })}
                          placeholder="https://example.com/logo.png"
                          className="text-sm"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
              </FeatureGate>
            </Card>
            
            {/* Document Header Preview - Full page width, outside the card */}
            {form.company_name && (
              <div className="mt-10 mb-8 p-6 bg-white border border-slate-200 rounded-lg shadow-sm">
                <p className="text-xs text-slate-400 mb-5 font-medium uppercase tracking-widest">Document Header Preview</p>
                {/* Large screens: side by side */}
                <div className="hidden sm:flex items-center justify-between pb-4 border-b-2 border-slate-300">
                  <div className="flex items-center gap-4">
                    {form.logo_url && (
                      <img src={form.logo_url} alt="" className="w-16 h-16 object-contain" />
                    )}
                    <p className="font-bold text-slate-800 text-xl">{form.company_name}</p>
                  </div>
                  {(form.business_phone || form.business_email || form.business_address || form.business_website) && (
                    <div className="pl-6 border-l-2 border-slate-300 text-right">
                      {form.business_phone && <p className="text-base text-slate-700 font-medium">{form.business_phone}</p>}
                      {form.business_email && <p className="text-base text-slate-600">{form.business_email}</p>}
                      {form.business_website && <p className="text-base text-indigo-600">{form.business_website}</p>}
                      {form.business_address && <p className="text-base text-slate-500">{form.business_address}</p>}
                    </div>
                  )}
                </div>
                {/* Small screens: stacked layout */}
                <div className="sm:hidden space-y-3 pb-4 border-b-2 border-slate-300">
                  <div className="flex items-center gap-3">
                    {form.logo_url && (
                      <img src={form.logo_url} alt="" className="w-14 h-14 object-contain" />
                    )}
                    <p className="font-bold text-slate-800 text-lg">{form.company_name}</p>
                  </div>
                  {(form.business_phone || form.business_email || form.business_address || form.business_website) && (
                    <div className="pt-3 border-t border-slate-200">
                      {form.business_phone && <p className="text-base text-slate-700">{form.business_phone}</p>}
                      {form.business_email && <p className="text-base text-slate-600">{form.business_email}</p>}
                      {form.business_website && <p className="text-base text-indigo-600">{form.business_website}</p>}
                      {form.business_address && <p className="text-base text-slate-500">{form.business_address}</p>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="space-y-6 pb-24">
            <Card className="p-6">
              <h3 className="font-heading font-semibold mb-4 text-lg">Form Settings</h3>
              <div className="space-y-6 max-w-2xl">
                <div className="space-y-2">
                  <Label>Form Title</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    onBlur={(e) => handleFormSettingsUpdate({ title: e.target.value })}
                    data-testid="form-title-setting"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={form.description || ''}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    onBlur={(e) => handleFormSettingsUpdate({ description: e.target.value })}
                    rows={3}
                    data-testid="form-description-setting"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Submit Button Text</Label>
                  <Input
                    value={form.submit_button_text}
                    onChange={(e) => setForm({ ...form, submit_button_text: e.target.value })}
                    onBlur={(e) => handleFormSettingsUpdate({ submit_button_text: e.target.value })}
                    data-testid="submit-text-setting"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Success Message</Label>
                  <Textarea
                    value={form.success_message}
                    onChange={(e) => setForm({ ...form, success_message: e.target.value })}
                    onBlur={(e) => handleFormSettingsUpdate({ success_message: e.target.value })}
                    rows={3}
                    data-testid="success-message-setting"
                  />
                </div>

                {/* Multi-step Forms Toggle */}
                <FeatureGate
                  requiredPlan="growth"
                  currentPlan={currentPlan}
                  featureName="Multi-step Forms"
                  variant="disabled"
                >
                <div className="flex items-center justify-between py-3 border-t">
                  <div>
                    <Label className="text-base flex items-center">
                      <Layers className="w-4 h-4 mr-2" />
                      Multi-step Form
                      {!hasFeatureAccess(currentPlan, 'growth') && (
                        <FeatureLockedBadge requiredPlan="growth" className="ml-2" />
                      )}
                    </Label>
                    <p className="text-sm text-slate-500">Break your form into multiple steps</p>
                  </div>
                  <Switch
                    checked={form.enable_steps || false}
                    onCheckedChange={(checked) => {
                      setForm({ ...form, enable_steps: checked });
                      handleFormSettingsUpdate({ enable_steps: checked });
                    }}
                    data-testid="multi-step-toggle"
                  />
                </div>
                </FeatureGate>

                {/* Password Protection */}
                <div className="flex items-center justify-between py-3 border-t">
                  <div>
                    <Label className="text-base flex items-center">
                      <Lock className="w-4 h-4 mr-2" />
                      Password Protection
                    </Label>
                    <p className="text-sm text-slate-500">Require a password to access this form</p>
                  </div>
                  <Switch
                    checked={form.password_protected || false}
                    onCheckedChange={(checked) => {
                      setForm({ ...form, password_protected: checked });
                      handleFormSettingsUpdate({ password_protected: checked });
                    }}
                    data-testid="password-protection-toggle"
                  />
                </div>
                {form.password_protected && (
                  <div className="space-y-2 ml-6 p-4 bg-slate-50 rounded-lg">
                    <Label>Form Password</Label>
                    <Input
                      type="password"
                      value={form.form_password || ''}
                      onChange={(e) => setForm({ ...form, form_password: e.target.value })}
                      onBlur={(e) => handleFormSettingsUpdate({ form_password: e.target.value })}
                      placeholder="Enter password"
                      data-testid="form-password-input"
                    />
                  </div>
                )}

                {/* Quiz Mode Settings */}
                <div className="flex items-center justify-between py-3 border-t">
                  <div>
                    <Label className="text-base flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
                      Quiz Mode
                    </Label>
                    <p className="text-sm text-slate-500">Score answers and show results</p>
                  </div>
                  <Switch
                    checked={form.is_quiz || false}
                    onCheckedChange={(checked) => {
                      setForm({ ...form, is_quiz: checked });
                      handleFormSettingsUpdate({ is_quiz: checked });
                    }}
                    data-testid="quiz-mode-toggle"
                  />
                </div>
                {form.is_quiz && (
                  <div className="space-y-4 ml-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2 mb-3">
                      <BarChart3 className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-800">Quiz Scoring Options</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">Show Results After Submit</Label>
                        <p className="text-xs text-slate-500">Display score after quiz completion</p>
                      </div>
                      <Switch
                        checked={form.show_results !== false}
                        onCheckedChange={(checked) => {
                          setForm({ ...form, show_results: checked });
                          handleFormSettingsUpdate({ show_results: checked });
                        }}
                        data-testid="show-results-toggle"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">Show Correct Answers</Label>
                        <p className="text-xs text-slate-500">Reveal correct answers after submit</p>
                      </div>
                      <Switch
                        checked={form.show_correct_answers || false}
                        onCheckedChange={(checked) => {
                          setForm({ ...form, show_correct_answers: checked });
                          handleFormSettingsUpdate({ show_correct_answers: checked });
                        }}
                        data-testid="show-correct-answers-toggle"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm">Passing Score (%)</Label>
                      <p className="text-xs text-slate-500">Minimum score to pass. Users above this will see a celebration!</p>
                      <Input
                        type="number"
                        value={form.passing_score || ''}
                        onChange={(e) => setForm({ ...form, passing_score: parseInt(e.target.value) || null })}
                        onBlur={(e) => handleFormSettingsUpdate({ passing_score: parseInt(e.target.value) || null })}
                        placeholder="e.g., 70 for 70%"
                        min={0}
                        max={100}
                        className="bg-white"
                        data-testid="passing-score-input"
                      />
                    </div>
                    
                    <div className="p-3 bg-amber-50 rounded border border-amber-200">
                      <p className="text-xs text-amber-700">
                        <strong>Tip:</strong> Set correct answers for each question in the Build tab. Select a field and find the "Quiz Scoring" section in Field Properties.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between py-3 border-t">
                  <div>
                    <Label className="text-base">Form Active</Label>
                    <p className="text-sm text-slate-500">Enable or disable form submissions</p>
                  </div>
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(checked) => {
                      setForm({ ...form, is_active: checked });
                      handleFormSettingsUpdate({ is_active: checked });
                    }}
                    data-testid="form-active-toggle"
                  />
                </div>
                <div className="flex items-center justify-between py-3 border-t">
                  <div>
                    <Label className="text-base">Email Notifications</Label>
                    <p className="text-sm text-slate-500">Receive email when someone submits</p>
                  </div>
                  <Switch
                    checked={form.enable_notifications}
                    onCheckedChange={(checked) => {
                      setForm({ ...form, enable_notifications: checked });
                      handleFormSettingsUpdate({ enable_notifications: checked });
                    }}
                    data-testid="notifications-toggle"
                  />
                </div>

                {/* GPS Location Capture */}
                <div className="flex items-center justify-between py-3 border-t">
                  <div>
                    <Label className="text-base flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                      Capture GPS Location
                    </Label>
                    <p className="text-sm text-slate-500">Record GPS coordinates when document is signed (for field agents)</p>
                  </div>
                  <Switch
                    checked={form.enable_location_capture || false}
                    onCheckedChange={(checked) => {
                      setForm({ ...form, enable_location_capture: checked });
                      handleFormSettingsUpdate({ enable_location_capture: checked });
                    }}
                    data-testid="location-capture-toggle"
                  />
                </div>

                {/* Remove Formlio Branding - Pro Only */}
                <FeatureGate
                  requiredPlan="pro"
                  currentPlan={currentPlan}
                  featureName="Remove Branding"
                  variant="disabled"
                >
                <div className="flex items-center justify-between py-3 mt-4 pt-4 border-t border-slate-200" data-testid="remove-branding-section">
                  <div>
                    <Label className="text-base flex items-center">
                      <Crown className="w-4 h-4 mr-2 text-amber-500" />
                      Remove Formlio Branding
                      {!hasFeatureAccess(currentPlan, 'pro') && (
                        <FeatureLockedBadge requiredPlan="pro" className="ml-2" />
                      )}
                    </Label>
                    <p className="text-sm text-slate-500">Hide "Powered by Formlio" from your form</p>
                  </div>
                  <Switch
                    checked={form.show_branding === false}
                    onCheckedChange={(checked) => {
                      setForm({ ...form, show_branding: !checked });
                      handleFormSettingsUpdate({ show_branding: !checked });
                    }}
                    data-testid="remove-branding-toggle"
                  />
                </div>
                </FeatureGate>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="integrations" className="space-y-6">
            <Card className="p-6">
              <IntegrationsPanel formId={formId} formFields={fields} formName={form?.title || 'Form'} />
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <Card className="p-6">
              <AnalyticsTab formId={formId} />
            </Card>
          </TabsContent>

          <TabsContent value="logs" className="space-y-6">
            <Card className="p-6">
              <FormActivityLogsViewer formId={formId} />
            </Card>
            <Card className="p-6">
              <IntegrationLogsViewer formId={formId} />
            </Card>
          </TabsContent>

          <TabsContent value="share" className="space-y-6">
            <Card className="p-6">
              <h3 className="font-heading font-semibold mb-4 text-lg">Share Your Form</h3>
              <div className="space-y-6 max-w-2xl">
                <div className="space-y-2">
                  <Label>Form Share Link <span className="text-xs text-slate-400 font-normal">(brand preview on WhatsApp / Slack / LinkedIn)</span></Label>
                  <div className="flex items-center space-x-2">
                    <Input value={shareUrl} readOnly className="font-mono text-sm" data-testid="form-url-display" />
                    <Button onClick={copyFormUrl} variant="outline" data-testid="copy-url-btn-share">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">Share this link anywhere — link previews automatically use your logo & company name.</p>
                </div>
                <div className="space-y-2">
                  <Label>Direct Form URL <span className="text-xs text-slate-400 font-normal">(use for embeds & direct browser visits)</span></Label>
                  <div className="flex items-center space-x-2">
                    <Input value={formUrl} readOnly className="font-mono text-sm" data-testid="form-direct-url-display" />
                    <Button
                      onClick={() => { navigator.clipboard.writeText(formUrl); toast.success('Direct URL copied'); }}
                      variant="outline"
                      data-testid="copy-direct-url-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Embed Code (iFrame)</Label>
                  <Textarea
                    value={`<iframe src="${formUrl}" width="100%" height="600" frameborder="0"></iframe>`}
                    readOnly
                    rows={3}
                    className="font-mono text-xs"
                    data-testid="embed-code"
                  />
                </div>
              </div>
            </Card>
            
            {/* Collaboration Section */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Form Collaboration
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Generate shareable links to let others view or edit this form
                  </p>
                </div>
                <Button onClick={() => setShowCollabModal(true)} data-testid="open-collab-modal-btn">
                  <Link2 className="w-4 h-4 mr-2" />
                  Manage Links
                </Button>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">
                  Create collaboration links with view-only or edit access. You can set expiration dates 
                  and revoke access at any time.
                </p>
              </div>
            </Card>
            
            {/* Collaboration Modal */}
            <FormCollaboration 
              formId={formId} 
              isOpen={showCollabModal} 
              onClose={() => setShowCollabModal(false)} 
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* RIGHT SIDEBAR: Field Properties — inline drawer on desktop, slide-in sheet on mobile */}
      {mobilePropsOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobilePropsOpen(false)}
          data-testid="mobile-props-overlay"
        />
      )}
      <div
        className={`${
          mobilePropsOpen
            ? 'fixed inset-y-0 right-0 z-50 w-[88%] max-w-[360px] block shadow-2xl'
            : 'hidden lg:block lg:w-72 lg:shrink-0 lg:sticky lg:top-20 lg:self-start'
        } h-[calc(100vh-110px)] lg:h-[calc(100vh-110px)] overflow-y-auto rounded-xl form-builder-scroll`}
        style={{
          backgroundColor: activeTab === 'build' ? '#1E293B' : 'transparent',
          height: mobilePropsOpen ? '100vh' : undefined,
        }}
      >
        {activeTab === 'build' && (
          <div className="p-4 pb-10">
            <h3 className="font-semibold mb-4 text-base flex items-center justify-between gap-2 text-white tracking-wide">
              <span className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-slate-400" />
                Field Properties
              </span>
              {/* Mobile-only close button */}
              <button
                type="button"
                onClick={() => setMobilePropsOpen(false)}
                className="lg:hidden text-slate-400 hover:text-white p-1 -mr-1"
                data-testid="mobile-props-close"
                aria-label="Close field properties"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </h3>
            {selectedFieldData ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-sm">Label</Label>
                  <Input
                    value={selectedFieldData.label}
                    onChange={(e) => updateField(selectedField, { label: e.target.value })}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-9 text-sm"
                    data-testid="field-label-input"
                  />
                </div>

                {/* Sublabels for composite fields (name, address) */}
                {selectedFieldData.type === 'name' && (
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm font-semibold">Sublabels</Label>
                    <div className="space-y-2 p-2.5 bg-slate-700/30 rounded-lg border border-slate-600/50">
                      {[
                        { key: 'first_name', default: 'First Name' },
                        { key: 'last_name', default: 'Last Name' },
                      ].map(sub => (
                        <div key={sub.key} className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-500 w-20 shrink-0">{sub.default}</span>
                          <Input
                            value={(selectedFieldData.sublabels || {})[sub.key] || sub.default}
                            onChange={(e) => updateField(selectedField, { sublabels: { ...(selectedFieldData.sublabels || {}), [sub.key]: e.target.value } })}
                            className="bg-slate-800 border-slate-600 text-white h-7 text-xs flex-1"
                          />
                        </div>
                      ))}
                    </div>
                    <Label className="text-slate-300 text-sm font-semibold mt-3">Placeholders</Label>
                    <div className="space-y-2 p-2.5 bg-slate-700/30 rounded-lg border border-slate-600/50">
                      {[
                        { key: 'first_name', default: 'e.g. John' },
                        { key: 'last_name', default: 'e.g. Smith' },
                      ].map(sub => (
                        <div key={sub.key} className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-500 w-20 shrink-0">{sub.key === 'first_name' ? 'First' : 'Last'}</span>
                          <Input
                            value={(selectedFieldData.sub_placeholders || {})[sub.key] || ''}
                            onChange={(e) => updateField(selectedField, { sub_placeholders: { ...(selectedFieldData.sub_placeholders || {}), [sub.key]: e.target.value } })}
                            placeholder={sub.default}
                            className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 h-7 text-xs flex-1"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedFieldData.type === 'address' && (
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm font-semibold">Sublabels</Label>
                    <div className="space-y-2 p-2.5 bg-slate-700/30 rounded-lg border border-slate-600/50">
                      {[
                        { key: 'country', default: 'Country' },
                        { key: 'street', default: 'Street Address' },
                        { key: 'state', default: 'State / Province' },
                        { key: 'city', default: 'City / Suburb' },
                        { key: 'zip', default: 'Postal / Zip Code' },
                      ].map(sub => (
                        <div key={sub.key} className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-500 w-20 shrink-0 truncate" title={sub.default}>{sub.default}</span>
                          <Input
                            value={(selectedFieldData.sublabels || {})[sub.key] || sub.default}
                            onChange={(e) => updateField(selectedField, { sublabels: { ...(selectedFieldData.sublabels || {}), [sub.key]: e.target.value } })}
                            className="bg-slate-800 border-slate-600 text-white h-7 text-xs flex-1"
                          />
                        </div>
                      ))}
                    </div>
                    <Label className="text-slate-300 text-sm font-semibold mt-3">Placeholders</Label>
                    <div className="space-y-2 p-2.5 bg-slate-700/30 rounded-lg border border-slate-600/50">
                      {[
                        { key: 'street', label: 'Street' },
                        { key: 'city', label: 'City' },
                        { key: 'state', label: 'State' },
                        { key: 'zip', label: 'Zip' },
                      ].map(sub => (
                        <div key={sub.key} className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-500 w-20 shrink-0">{sub.label}</span>
                          <Input
                            value={(selectedFieldData.sub_placeholders || {})[sub.key] || ''}
                            onChange={(e) => updateField(selectedField, { sub_placeholders: { ...(selectedFieldData.sub_placeholders || {}), [sub.key]: e.target.value } })}
                            placeholder={`e.g. Enter ${sub.label.toLowerCase()}`}
                            className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 h-7 text-xs flex-1"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Placeholder for non-composite fields */}
                {!['name', 'address', 'heading', 'divider', 'page_break', 'file', 'image', 'signature'].includes(selectedFieldData.type) && (
                  <div className="space-y-1.5">
                    <Label className="text-slate-400 text-sm">Placeholder</Label>
                    <Textarea
                      value={selectedFieldData.placeholder || ''}
                      onChange={(e) => updateField(selectedField, { placeholder: e.target.value })}
                      className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 text-sm min-h-[80px] resize-y"
                      placeholder={'e.g. Type your answer here...\nUse Enter to break lines'}
                      rows={3}
                      data-testid="field-placeholder-input"
                    />
                    <p className="text-[11px] text-slate-500">Text shown inside the field. Press Enter for new lines — line breaks are preserved on the published form.</p>
                  </div>
                )}

                {/* Hover Text (Tooltip) */}
                {!['heading', 'divider', 'page_break', 'static_text'].includes(selectedFieldData.type) && (
                  <div className="space-y-1.5">
                    <Label className="text-slate-400 text-sm">Hover Text</Label>
                    <Input
                      value={selectedFieldData.hover_text || ''}
                      onChange={(e) => updateField(selectedField, { hover_text: e.target.value })}
                      className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-9 text-sm"
                      placeholder="Tooltip on hover..."
                      data-testid="field-hover-text-input"
                    />
                    <p className="text-[11px] text-slate-500">Shows when user hovers this field</p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <Label className="text-slate-300 text-sm">Required</Label>
                  <Switch
                    checked={selectedFieldData.required}
                    onCheckedChange={(checked) => updateField(selectedField, { required: checked })}
                    data-testid="field-required-toggle"
                  />
                </div>

                {/* Read Only */}
                {!['heading', 'divider', 'page_break', 'html', 'static_text'].includes(selectedFieldData.type) && (
                  <div className="space-y-2 pt-3 border-t border-slate-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-slate-300 text-sm font-semibold">Read Only</Label>
                        <p className="text-[11px] text-slate-500 mt-0.5">Prevent entry in this field</p>
                      </div>
                      <Switch
                        checked={selectedFieldData.read_only || false}
                        onCheckedChange={(checked) => updateField(selectedField, { read_only: checked })}
                        data-testid="field-readonly-toggle"
                      />
                    </div>
                    {selectedFieldData.read_only && (
                      <div className="space-y-1.5 mt-2">
                        <Label className="text-slate-400 text-xs">Default Value</Label>
                        <Textarea
                          value={selectedFieldData.default_value || ''}
                          onChange={(e) => updateField(selectedField, { default_value: e.target.value })}
                          placeholder={'Enter fixed value...\nUse Enter for separate lines'}
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 text-sm min-h-[100px] resize-y font-mono"
                          rows={4}
                          data-testid="field-default-value-input"
                        />
                        <p className="text-[11px] text-slate-500">This value will be pre-filled and locked. Line breaks are preserved when displayed.</p>
                        {!selectedFieldData.default_value && selectedFieldData.placeholder && (
                          <p className="text-[11px] text-amber-300/90 leading-snug bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5">
                            <strong>Tip:</strong> Default Value is empty — your Placeholder content will be shown instead on the published form.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Shrink (Field Width) */}
                {!['heading', 'divider', 'page_break', 'html'].includes(selectedFieldData.type) && (
                  <div className="space-y-2 pt-3 border-t border-slate-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-slate-300 text-sm font-semibold">Shrink</Label>
                        <p className="text-[11px] text-slate-500 mt-0.5">Make field smaller</p>
                      </div>
                      <Switch
                        checked={(selectedFieldData.width || 'full') === 'half'}
                        onCheckedChange={(checked) => {
                          const updates = { width: checked ? 'half' : 'full' };
                          if (checked) {
                            updates.new_row = false;
                          } else {
                            updates.new_row = false;
                          }
                          updateField(selectedField, updates);
                        }}
                        data-testid="field-shrink-toggle"
                      />
                    </div>
                    {(selectedFieldData.width === 'half' || selectedFieldData.width === 'third') && (
                      <div className="flex items-center justify-between mt-2">
                        <div>
                          <Label className="text-slate-300 text-sm font-semibold">Move to a new line</Label>
                          <p className="text-[11px] text-slate-500 mt-0.5">Move field to a new line</p>
                        </div>
                        <Switch
                          checked={selectedFieldData.new_row || false}
                          onCheckedChange={(checked) => updateField(selectedField, { new_row: checked })}
                          data-testid="field-new-row-toggle"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Options for select/radio/checkbox/multiple */}
                {(selectedFieldData.type === 'select' || selectedFieldData.type === 'radio' || selectedFieldData.type === 'checkbox' || selectedFieldData.type === 'multiple') && (
                  <div className="space-y-1.5">
                    <Label className="text-slate-400 text-sm">Options</Label>
                    <div className="space-y-2">
                      {(selectedFieldData.options || []).map((option, index) => {
                        const isMultipleAnswer = selectedFieldData.type === 'checkbox' || selectedFieldData.type === 'multiple';
                        const correctAnswers = isMultipleAnswer ? (selectedFieldData.correct_answers || []) : null;
                        const isCorrect = isMultipleAnswer ? correctAnswers.includes(option) : selectedFieldData.correct_answer === option;
                        // ── "Default selected" toggle support ────────────────────────────
                        // For multi-select (checkbox/multiple) we store an array in
                        // ``default_values``. For single-select (radio/select) we store
                        // a single string in ``default_value``.
                        const defaultValues = Array.isArray(selectedFieldData.default_values)
                          ? selectedFieldData.default_values
                          : [];
                        const isDefault = isMultipleAnswer
                          ? defaultValues.includes(option)
                          : selectedFieldData.default_value === option;
                        const handleSetDefault = () => {
                          if (isMultipleAnswer) {
                            const next = defaultValues.includes(option)
                              ? defaultValues.filter((v) => v !== option)
                              : [...defaultValues, option];
                            updateField(selectedField, { default_values: next });
                          } else {
                            // Toggle off if clicking the already-default option
                            updateField(selectedField, {
                              default_value: selectedFieldData.default_value === option ? '' : option,
                            });
                          }
                        };
                        
                        const handleSetCorrect = () => {
                          if (isMultipleAnswer) {
                            const newCorrectAnswers = correctAnswers.includes(option)
                              ? correctAnswers.filter(a => a !== option)
                              : [...correctAnswers, option];
                            updateField(selectedField, { correct_answers: newCorrectAnswers });
                          } else {
                            updateField(selectedField, { correct_answer: option });
                          }
                        };
                        
                        return (
                          <div key={index} className="flex items-center gap-2">
                            <Input
                              value={option}
                              onChange={(e) => {
                                const newOptions = [...(selectedFieldData.options || [])];
                                const oldOption = newOptions[index];
                                newOptions[index] = e.target.value;
                                updateField(selectedField, { options: newOptions });
                                if (isMultipleAnswer && correctAnswers.includes(oldOption)) {
                                  const newCorrectAnswers = correctAnswers.map(a => a === oldOption ? e.target.value : a);
                                  updateField(selectedField, { correct_answers: newCorrectAnswers });
                                }
                              }}
                              placeholder={`Option ${index + 1}`}
                              className="flex-1 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 h-8 text-sm"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const newOptions = [...(selectedFieldData.options || [])];
                                  newOptions.splice(index + 1, 0, '');
                                  updateField(selectedField, { options: newOptions });
                                }
                              }}
                            />
                            {form?.is_quiz && (
                              <Badge 
                                variant={isCorrect ? "default" : "outline"}
                                className={`text-xs cursor-pointer whitespace-nowrap ${isCorrect ? 'bg-green-500 hover:bg-green-600' : 'hover:bg-slate-100'}`}
                                onClick={handleSetCorrect}
                                data-testid={`set-correct-${index}`}
                              >
                                {isCorrect ? '✓ Correct' : 'Set Correct'}
                              </Badge>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleSetDefault}
                              title={isDefault ? 'Pre-selected by default' : 'Mark as default selection'}
                              className={`px-2 h-8 ${isDefault ? 'text-amber-400 hover:text-amber-300' : 'text-slate-400 hover:text-slate-200'}`}
                              data-testid={`set-default-${index}`}
                            >
                              <Star className={`w-4 h-4 ${isDefault ? 'fill-amber-400' : ''}`} />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newOptions = (selectedFieldData.options || []).filter((_, i) => i !== index);
                                const updates = { options: newOptions.length > 0 ? newOptions : ['Option 1'] };
                                if (isMultipleAnswer) {
                                  updates.correct_answers = correctAnswers.filter(a => a !== option);
                                  if (Array.isArray(selectedFieldData.default_values)) {
                                    updates.default_values = selectedFieldData.default_values.filter(v => v !== option);
                                  }
                                } else if (selectedFieldData.default_value === option) {
                                  updates.default_value = '';
                                }
                                updateField(selectedField, updates);
                              }}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newOptions = [...(selectedFieldData.options || []), `Option ${(selectedFieldData.options || []).length + 1}`];
                          updateField(selectedField, { options: newOptions });
                        }}
                        className="w-full text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Option
                      </Button>
                    </div>
                  </div>
                )}

                {/* Image Choice Options */}
                {selectedFieldData.type === 'image_choice' && (
                  <div className="space-y-2">
                    <Label>Image Options</Label>
                    <div className="space-y-3">
                      {(selectedFieldData.image_options || []).map((option, index) => (
                        <div key={index} className="border rounded-lg p-3 space-y-2 bg-slate-50">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-500">Option {index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newOptions = (selectedFieldData.image_options || []).filter((_, i) => i !== index);
                                updateField(selectedField, { image_options: newOptions.length > 0 ? newOptions : [{ label: 'Option 1', image_url: '' }] });
                              }}
                              className="ml-auto text-red-500 hover:text-red-700 hover:bg-red-50 px-2 h-6"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <Input
                            value={option.label || ''}
                            onChange={(e) => {
                              const newOptions = [...(selectedFieldData.image_options || [])];
                              newOptions[index] = { ...newOptions[index], label: e.target.value };
                              updateField(selectedField, { image_options: newOptions });
                            }}
                            placeholder="Label"
                            className="text-sm"
                          />
                          <Input
                            value={option.image_url || ''}
                            onChange={(e) => {
                              const newOptions = [...(selectedFieldData.image_options || [])];
                              newOptions[index] = { ...newOptions[index], image_url: e.target.value };
                              updateField(selectedField, { image_options: newOptions });
                            }}
                            placeholder="Image URL (https://...)"
                            className="text-sm"
                          />
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newOptions = [...(selectedFieldData.image_options || []), { label: `Option ${(selectedFieldData.image_options || []).length + 1}`, image_url: '' }];
                          updateField(selectedField, { image_options: newOptions });
                        }}
                        className="w-full"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Image Option
                      </Button>
                    </div>
                  </div>
                )}

                {/* Icon Choice Options */}
                {selectedFieldData.type === 'icon_choice' && (
                  <div className="space-y-2">
                    <Label>Icon Options</Label>
                    <div className="space-y-3">
                      {(selectedFieldData.icon_options || []).map((option, index) => (
                        <div key={index} className="border rounded-lg p-3 space-y-2 bg-slate-50">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-500">Option {index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newOptions = (selectedFieldData.icon_options || []).filter((_, i) => i !== index);
                                updateField(selectedField, { icon_options: newOptions.length > 0 ? newOptions : [{ label: 'Option 1', icon: 'star' }] });
                              }}
                              className="ml-auto text-red-500 hover:text-red-700 hover:bg-red-50 px-2 h-6"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <Input
                            value={option.label || ''}
                            onChange={(e) => {
                              const newOptions = [...(selectedFieldData.icon_options || [])];
                              newOptions[index] = { ...newOptions[index], label: e.target.value };
                              updateField(selectedField, { icon_options: newOptions });
                            }}
                            placeholder="Label"
                            className="text-sm"
                          />
                          <IconPicker
                            selectedIcon={option.icon?.value || option.icon || 'star'}
                            selectedType={option.icon?.type || 'image'}
                            onSelect={(icon) => {
                              const newOptions = [...(selectedFieldData.icon_options || [])];
                              newOptions[index] = { ...newOptions[index], icon };
                              updateField(selectedField, { icon_options: newOptions });
                            }}
                          />
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newOptions = [...(selectedFieldData.icon_options || []), { label: `Option ${(selectedFieldData.icon_options || []).length + 1}`, icon: 'star' }];
                          updateField(selectedField, { icon_options: newOptions });
                        }}
                        className="w-full"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Icon Option
                      </Button>
                    </div>
                  </div>
                )}

                {/* Conditional Logic */}
                <div className="pt-4 border-t border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="flex items-center text-slate-300 text-sm">
                      <GitBranch className="w-4 h-4 mr-2 text-slate-400" />
                      Conditional Logic
                    </Label>
                    <Switch
                      checked={!!selectedFieldData.condition}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          const otherFields = fields.filter(f => f.id !== selectedField);
                          if (otherFields.length > 0) {
                            updateField(selectedField, { 
                              condition: { field_id: otherFields[0].id, operator: 'equals', value: '' } 
                            });
                          } else {
                            toast.error('Add other fields first to use conditional logic');
                          }
                        } else {
                          updateField(selectedField, { condition: null });
                        }
                      }}
                    />
                  </div>
                  
                  {selectedFieldData.condition && (
                    <div className="space-y-3 p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                      <p className="text-xs font-medium text-slate-300">Show this field when:</p>
                      
                      <Select
                        value={selectedFieldData.condition.field_id}
                        onValueChange={(value) => updateField(selectedField, { 
                          condition: { ...selectedFieldData.condition, field_id: value } 
                        })}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white text-sm">
                          <SelectValue placeholder="Select field" />
                        </SelectTrigger>
                        <SelectContent>
                          {fields.filter(f => f.id !== selectedField).map(f => (
                            <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Select
                        value={selectedFieldData.condition.operator}
                        onValueChange={(value) => updateField(selectedField, { 
                          condition: { ...selectedFieldData.condition, operator: value } 
                        })}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {conditionOperators.map(op => (
                            <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Input
                        value={selectedFieldData.condition.value}
                        onChange={(e) => updateField(selectedField, { 
                          condition: { ...selectedFieldData.condition, value: e.target.value } 
                        })}
                        placeholder="Enter value to match"
                        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">Select a field to edit its properties</p>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
      
      {/* Mobile FAB — opens the Form Elements palette as a slide-up sheet */}
      {activeTab === 'build' && (
        <button
          type="button"
          onClick={() => setMobilePaletteOpen(true)}
          aria-label="Add a form field"
          data-testid="mobile-add-field-fab"
          className="lg:hidden fixed bottom-24 right-5 z-[60] w-14 h-14 rounded-full bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white shadow-2xl shadow-indigo-500/40 flex items-center justify-center transition-all"
        >
          <Plus className="w-7 h-7" strokeWidth={2.4} />
        </button>
      )}

      {/* Mobile Form Elements palette sheet */}
      <Sheet open={mobilePaletteOpen} onOpenChange={setMobilePaletteOpen}>
        <SheetContent
          side="bottom"
          className="lg:hidden h-[80vh] bg-slate-900 border-slate-700 p-0 overflow-y-auto"
          data-testid="mobile-palette-sheet"
        >
          <SheetHeader className="p-4 border-b border-slate-700 text-left flex flex-row items-center justify-between sticky top-0 z-10 bg-slate-900">
            <SheetTitle className="text-white text-base m-0">Add a field</SheetTitle>
            <button
              type="button"
              onClick={() => setMobilePaletteOpen(false)}
              aria-label="Close field palette"
              data-testid="mobile-palette-close"
              className="w-9 h-9 rounded-full bg-slate-700 hover:bg-slate-600 active:scale-95 text-white flex items-center justify-center transition-all"
            >
              <XCircle className="w-5 h-5" strokeWidth={2.2} />
            </button>
          </SheetHeader>
          {formElementsPaletteContent}
        </SheetContent>
      </Sheet>

      {/* Branding Banner - for all users */}
      <BrandingBanner userPlan={user?.subscription?.plan || 'free'} isAuthenticated={true} />
    </DashboardLayout>
  );
};

// Integration Logs Viewer Component
const IntegrationLogsViewer = ({ formId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const loadLogs = async () => {
    try {
      const response = await integrationsAPI.getFormLogs(formId);
      setLogs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'pending':
      case 'retrying':
        return <RefreshCw className="w-4 h-4 text-amber-600 animate-spin" />;
      default:
        return <AlertCircle className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-amber-100 text-amber-800',
      retrying: 'bg-amber-100 text-amber-800',
    };
    return variants[status] || 'bg-slate-100 text-slate-800';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const formatIntegrationType = (type) => {
    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Integration Logs</h3>
          <p className="text-sm text-slate-600">View integration execution history for this form</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadLogs} data-testid="refresh-logs-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {(!logs || logs.length === 0) ? (
        <div className="text-center py-12 text-slate-500">
          <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>No integration logs yet</p>
          <p className="text-sm">Logs will appear here when integrations are triggered by form submissions</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(logs || []).map((log, index) => (
            <div 
              key={log.id || index} 
              className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
              data-testid={`log-entry-${index}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(log.status)}
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{formatIntegrationType(log.integration_type)}</span>
                      <Badge className={getStatusBadge(log.status)}>
                        {log.status}
                      </Badge>
                      {log.retry_count > 0 && (
                        <Badge variant="outline">
                          Retry #{log.retry_count}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      Submission: {log.submission_id?.substring(0, 8)}...
                    </p>
                  </div>
                </div>
                <div className="text-right text-sm text-slate-500">
                  <p>{formatDate(log.created_at)}</p>
                  {log.completed_at && (
                    <p className="text-xs">Completed: {formatDate(log.completed_at)}</p>
                  )}
                </div>
              </div>
              
              {log.error_message && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  <strong>Error:</strong> {log.error_message}
                </div>
              )}
              
              {log.response_data && log.status === 'success' && (
                <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                  <strong>Success:</strong> Integration executed successfully
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// Form Activity Logs Viewer — compact, single-line entries inside one scroll container
const FormActivityLogsViewer = ({ formId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await formsAPI.getActivityLogs(formId, 200);
      setLogs(Array.isArray(response.data?.logs) ? response.data.logs : []);
    } catch (error) {
      console.error('Failed to load form activity logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  const ACTION_META = {
    form_created:    { label: 'Created',    color: 'text-green-700  bg-green-50',  dot: 'bg-green-500' },
    form_updated:    { label: 'Updated',    color: 'text-blue-700   bg-blue-50',   dot: 'bg-blue-500' },
    form_deleted:    { label: 'Deleted',    color: 'text-red-700    bg-red-50',    dot: 'bg-red-500' },
    form_duplicated: { label: 'Duplicated', color: 'text-purple-700 bg-purple-50', dot: 'bg-purple-500' },
  };

  // Build a one-line summary for any log entry
  const summarizeLog = (log) => {
    const d = log.details || {};
    if (log.action === 'form_created') return `"${d.title || 'Untitled'}"${d.from_template ? ' from template' : ''}`;
    if (log.action === 'form_deleted') return `"${d.title || 'form'}" — ${d.kept_data ? 'data kept' : 'all data removed'}`;
    if (log.action === 'form_duplicated') return `→ "${d.title || ''}"`;
    if (log.action === 'form_updated') {
      const parts = [];
      if (d.title_changed) parts.push(`title: "${d.title_changed.from}" → "${d.title_changed.to}"`);
      if (d.fields) {
        if ((d.fields.added || []).length) parts.push(`+${d.fields.added.length} field${d.fields.added.length > 1 ? 's' : ''} (${d.fields.added.map(a => a.label || a.type).join(', ')})`);
        if ((d.fields.removed || []).length) parts.push(`−${d.fields.removed.length} removed`);
        if ((d.fields.modified || []).length) parts.push(`✎${d.fields.modified.length} edited`);
      }
      if (d.theme_changed) parts.push('theme');
      if (d.branding_changed) parts.push('branding');
      ['is_active', 'password_protected', 'expiration_date',
       'show_branding', 'is_quiz', 'enable_steps',
       'enable_notifications', 'enable_location_capture'].forEach(k => {
        if (d[k]) parts.push(`${k.replace(/_/g, ' ')}=${String(d[k].to)}`);
      });
      return parts.join(' · ') || 'minor changes';
    }
    return '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Form Activity</h3>
          <p className="text-sm text-slate-600">Audit trail of every change to this form</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadLogs} data-testid="refresh-activity-logs-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {(!logs || logs.length === 0) ? (
        <div className="text-center py-10 text-slate-500 border border-dashed border-slate-200 rounded-lg" data-testid="empty-activity-logs">
          <History className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">No activity yet — edits to this form will appear here</p>
        </div>
      ) : (
        <div
          className="border border-slate-200 rounded-lg max-h-[420px] overflow-y-auto divide-y divide-slate-100 form-builder-scroll bg-white"
          data-testid="activity-logs-list"
        >
          {logs.map((log) => {
            const meta = ACTION_META[log.action] || { label: log.action, color: 'text-slate-700 bg-slate-50', dot: 'bg-slate-400' };
            const summary = summarizeLog(log);
            return (
              <div
                key={log.id}
                className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 transition-colors"
                data-testid={`activity-log-${log.id}`}
              >
                <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${meta.color}`}>
                  {meta.label}
                </span>
                <span className="shrink-0 text-slate-700 font-medium truncate max-w-[140px]" title={log.user_name || log.user_email || ''}>
                  {log.user_name || log.user_email || 'Unknown user'}
                </span>
                <span className="flex-1 text-slate-600 truncate" title={summary}>
                  {summary}
                </span>
                <span className="shrink-0 text-xs text-slate-400 whitespace-nowrap" title={log.timestamp}>
                  {formatTime(log.timestamp)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
